/**
 * CADUploadZone - Drag & drop file upload component for CAD files
 */

import React, { useState, useCallback, useRef } from 'react';
import { UploadCloud, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import CadLimitsPanel from '@components/cad/CadLimitsPanel';
import { useCapabilities } from '@hooks/useCapabilities';
import { cn } from '@/lib/utils';
import { makeCadFileSchema } from '@/features/cad/schemas/cadFile.schema';
import { uploadCADFile } from '../../services/cad.service';
import { CADModel } from '../../types/cad';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import CADClassificationFields from './CADClassificationFields';
import {
  EMPTY_CLASSIFICATION_DRAFT,
  classificationFromDraft,
  type CADClassificationDraft,
} from './classification';

interface CADUploadZoneProps {
  onUploadComplete: (model: CADModel) => void;
}

const CADUploadZone: React.FC<CADUploadZoneProps> = ({ onUploadComplete }) => {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [classification, setClassification] = useState<CADClassificationDraft>(
    EMPTY_CLASSIFICATION_DRAFT,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const cad = capabilities.status === 'ready' ? capabilities.capabilities?.cad : undefined;
  const canSelect = Boolean(cad?.enabled);
  const validateFile = useCallback((file: File): string | null => {
    // Without the limits there is nothing to validate against; the zone stays disabled.
    if (!cad || !canSelect) return t('upload.limitsUnavailable');
    const result = makeCadFileSchema(cad).safeParse(file);
    return result.success ? null : result.error.issues[0].message;
  }, [cad, canSelect, t]);

  const handleUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      const model = await uploadCADFile(
        file,
        (p) => setProgress(p),
        classificationFromDraft(classification),
      );
      onUploadComplete(model);
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'cad.upload.failed' }));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [classification, onUploadComplete, t, validateFile]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleUpload(file);
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClick = () => {
    if (canSelect && !uploading) fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  return (
    <div>
      <details className="mb-2 rounded-lg border border-border p-2">
        <summary className="cursor-pointer text-xs font-semibold">
          {t('cad.classification.optionalTitle')}
        </summary>
        <div className="mt-2">
          <CADClassificationFields
            value={classification}
            onChange={setClassification}
            idPrefix="cad-upload-classification"
            disabled={uploading || !canSelect}
          />
        </div>
      </details>
      {/* A button, not a div: the pre-port zone showed a pointer cursor with no
          keyboard path to the file picker. */}
      <CadLimitsPanel
        status={capabilities.status}
        cad={capabilities.capabilities?.cad}
        onRetry={capabilities.retry}
      />
      <button
        type="button"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        disabled={uploading || !canSelect}
        className={cn(
          'flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed p-6 text-center',
          'transition-[border-color,background-color,opacity] duration-150 ease-shell',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
          isDragOver
            ? 'border-primary bg-accent'
            : 'border-border bg-transparent hover:border-primary/50 hover:bg-accent/60',
          uploading && 'pointer-events-none opacity-60',
        )}
      >
        <UploadCloud
          aria-hidden="true"
          strokeWidth={1.5}
          className="size-10 text-muted-foreground"
        />
        <span className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('cad.upload.dropLabel')}
        </span>
        {canSelect && cad && (
          <span className="text-xs leading-[1.5] text-muted-foreground">
            {cad.allowedExtensions.join(', ')}
          </span>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={canSelect ? cad?.allowedExtensions.join(',') : undefined}
        disabled={uploading || !canSelect}
        onChange={handleFileChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {uploading && (
        <div className="mt-2">
          <Progress value={progress} className="h-1.5" />
          <p className="revy-cjk revy-figure mt-1 text-xs leading-[1.5] text-muted-foreground">
            {t('cad.upload.processing', { progress })}
          </p>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mt-2 flex items-start gap-2 pr-2">
          <AlertDescription className="revy-cjk flex-1 whitespace-pre-line leading-[1.6]">
            {error}
          </AlertDescription>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={() => setError(null)}
            className="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3.5" strokeWidth={2.2} />
          </button>
        </Alert>
      )}
    </div>
  );
};

export default CADUploadZone;
